// ─────────────────────────────────────────────
//  RemoveFromDay — take ONE person off today's roster.
//
//  The Daily list could add a person and could clear the WHOLE day, and had
//  nothing in between. So a name added by mistake — or five dummy accounts left
//  behind after a test — stayed on the roster until somebody wiped the entire
//  board, which is not a thing anyone will do at 07:00 to remove one person.
//
//  ⚠️ THIS IS NOT "SEND HOME". Departing a provider records that a real person
//  finished their shift; the board keeps them, in the Home section, because that
//  is a fact about the day. Removing says they were never on this day's list at
//  all. Two different truths, so two different controls — and the destructive
//  one is the one that has to explain itself.
//
//  GUARDS, both of which refuse rather than repair:
//   · Somebody STAFFING A ROOM cannot be removed. The board would keep showing
//     them in that room while the roster denied they were on today at all, and
//     every derivation that reconciles the two (the bench, the break board,
//     coverage) would disagree about the same person. Clear the room first.
//   · The AIC cannot be removed. `aicId` would point at somebody who is no
//     longer on the roster, and the day would have no one in charge.
//
//  ⚠️ THE MEMBER-ID MAPS ARE PURGED WITH THEM. `departed` and `givingBreaks`
//  are keyed by member id and are merged, not replaced, on write — so a stale
//  entry survives the removal and springs back the moment that person is added
//  again ("GIVING BREAKS" on somebody who just walked in). They are explicitly
//  set false rather than left behind.
//
//  Two-click confirm, no modal — the same shape the page's Clear Day uses, and
//  the second click is what makes it deliberate.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import { useToast } from "../context/ToastContext";
import { setDailyRoster } from "../firebase/firestore";
import { roomForOrRoom } from "../domain/gridTemplate";
import tokens from "../theme/tokens";

const { color, radius, font } = tokens;

// Every assignment row that names this person, whatever seat they hold. An
// orientee counts: they are standing in that room too.
//
// ⚠️ THE ROOM IS NAMED THE WAY THE BOARD NAMES IT. An assignment's `orRoom`
// drops the "OR " prefix by convention, so reading it raw produced "In 2" on a
// board whose rooms are called "OR 2" — a message about a room the reader has
// to translate. `rooms` resolves it to the live room's own name; without the
// list the raw label is still returned rather than a guess.
export function roomsHolding(person, assignments = [], rooms = []) {
  const id = person?.id;
  const key = String(person?.name || "").trim().toLowerCase();
  return (Array.isArray(assignments) ? assignments : [])
    .filter((a) => {
      if (!a) return false;
      if (id && (a.ahpId === id || a.attendingId === id || a.orienteeId === id)) return true;
      if (!key) return false;
      const named = [a.ahpName, a.attendingName, a.orienteeName]
        .map((n) => String(n || "").split(",")[0].trim().toLowerCase());
      return named.includes(key);
    })
    .map((a) => roomForOrRoom(a.orRoom, rooms)?.name || a.orRoom)
    .filter(Boolean);
}

export default function RemoveFromDay({
  person,
  roster,
  assignments = [],
  rooms = [],
  facilityId,
  date,
  actor = null,
  canManage = false,
  onRemoved = null,
}) {
  const toast = useToast();
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!canManage || !person?.id) return null;

  const isAic = roster?.aicId === person.id;
  const held = roomsHolding(person, assignments, rooms);

  const remove = async () => {
    if (isAic) {
      toast.error(`${person.name} is today's AIC — hand that off first.`);
      setArmed(false);
      return;
    }
    if (held.length) {
      toast.error(
        `${person.name} is still in ${held.join(", ")}. Clear the room first, or send them home instead.`
      );
      setArmed(false);
      return;
    }
    setBusy(true);
    try {
      const inCrnas = (roster?.crnas || []).some((c) => c && c.id === person.id);
      const list = inCrnas ? "crnas" : "supervisors";
      const next = (roster?.[list] || []).filter((m) => m && m.id !== person.id);
      await setDailyRoster(
        facilityId || "default",
        date,
        {
          [list]: next,
          // Purged, not left behind: these maps merge on write, so a stale
          // entry would spring back if this person is ever added again.
          departed: { [person.id]: false },
          givingBreaks: { [person.id]: false },
        },
        actor
      );
      toast.success(`${person.name} removed from today.`);
      onRemoved && onRemoved(person);
    } catch (e) {
      console.error("Remove from day failed:", e);
      toast.error("Could not remove them from today.");
    } finally {
      setBusy(false);
      setArmed(false);
    }
  };

  // The blocked reason is shown BEFORE the first click, not after it: a control
  // that arms and then refuses teaches people to click twice and read nothing.
  const blocked = isAic ? "Today's AIC" : held.length ? `In ${held.join(", ")}` : "";

  return (
    <span style={st.wrap}>
      {blocked && <span style={st.why}>{blocked}</span>}
      <button
        type="button"
        disabled={busy || Boolean(blocked)}
        onClick={() => (armed ? remove() : setArmed(true))}
        onBlur={() => setArmed(false)}
        title={blocked ? `${blocked} — cannot remove` : `Remove ${person.name} from today's list`}
        aria-label={
          armed ? `Confirm removing ${person.name} from today` : `Remove ${person.name} from today`
        }
        style={{ ...st.btn, ...(armed ? st.armed : null), ...(blocked ? st.off : null) }}
      >
        {busy ? "…" : armed ? "Sure?" : "Remove"}
      </button>
    </span>
  );
}

const st = {
  wrap: { display: "inline-flex", alignItems: "center", gap: 6 },
  why: { fontSize: 10.5, color: color.text.muted, whiteSpace: "nowrap" },
  btn: {
    background: "transparent",
    border: `1px solid ${color.border.default}`,
    color: color.text.secondary,
    borderRadius: radius.sm,
    padding: "4px 9px",
    fontSize: 12,
    fontWeight: font.weight.bold,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  // Armed is the only red: the resting state must not read as an alarm on a
  // row that is simply sitting there.
  armed: { borderColor: color.accent.error, color: color.accent.error },
  off: { opacity: 0.5, cursor: "not-allowed" },
};
